import { isDeviceEligibleForProgram } from '@/hooks/useEligibleForProgram'
import { selectProgram } from '@/reducers/programsNew'
import {
  DeviceEligibilityOverview,
  EnrollmentOverview,
  EnrollmentRoot,
} from '@/types/programs-new/programs-new'
import {
  getDeviceEnrollment,
  hasSomeDeviceEnrolled,
} from '@/utils/utility-program-helpers/utilityProgramHelpers'
import { P, match } from 'ts-pattern'
import { useAppSelector } from '@/hooks'
import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'

export enum VerificationStatusTagType {
  ENROLLED = 'Enrolled',
  ELIGIBLE = 'Eligible',
  AVAILABLE = 'Available',
  INELIGIBLE = 'Ineligible',
  PENDING = 'Pending',
  COMPLETED = 'Completed',
  INACTIVE = 'Program Ended',
}

export function useDeviceVerificationStatus(props: {
  programId: number
  deviceId?: number | string
  deviceType?: DeviceType
}) {
  const programEnrollment = useAppSelector(selectProgram(props.programId))
  return getDeviceVerificationStatus(
    programEnrollment?.device_eligibility_overview,
    programEnrollment?.enrollment_overview,
    programEnrollment?.utility_program.active ?? true,
    props.deviceId,
    props.deviceType,
  )
}

export function getDeviceVerificationStatus(
  deviceEligibilityOverview: DeviceEligibilityOverview | undefined,
  enrollmentOverview: EnrollmentOverview | undefined | null,
  programActive: boolean,
  deviceId?: number | string,
  deviceType?: DeviceType,
) {
  const {
    deviceEligibility: deviceIsEligible,
    profileEligibility: profileIsEligible,
  } = isDeviceEligibleForProgram(
    deviceEligibilityOverview,
    deviceId,
    deviceType,
  )

  const profileStatus =
    enrollmentOverview?.program_enrollment?.verification_status

  const deviceEnrollment: EnrollmentRoot | undefined = match({
    deviceId,
    deviceType,
  })
    .with({ deviceId: P.nonNullable, deviceType: P.nonNullable }, (params) =>
      getDeviceEnrollment(
        enrollmentOverview ? enrollmentOverview : undefined,
        params.deviceId,
        params.deviceType,
      ),
    )
    .otherwise(() => undefined)

  const deviceEnrolled = deviceEnrollment?.enrolled ?? false

  if (!programActive) {
    if (deviceEnrolled && profileStatus === 'VERIFIED') {
      return { deviceStatus: VerificationStatusTagType.COMPLETED }
    }
    return { deviceStatus: VerificationStatusTagType.INACTIVE }
  }

  if (deviceEnrolled) {
    if (profileStatus === 'VERIFIED') {
      return { deviceStatus: VerificationStatusTagType.ENROLLED, profileStatus }
    }
    return { deviceStatus: VerificationStatusTagType.PENDING, profileStatus }
  }

  // Homes get special logic here since the "Home" tab isn't used for behavioral enrollments
  // so much as a catch all for program management
  if (deviceType === 'home' && hasSomeDeviceEnrolled(enrollmentOverview)) {
    return { deviceStatus: VerificationStatusTagType.ENROLLED, profileStatus }
  }

  if (!deviceIsEligible) {
    return { deviceStatus: VerificationStatusTagType.INELIGIBLE, profileStatus }
  }

  if (profileIsEligible) {
    return { deviceStatus: VerificationStatusTagType.ELIGIBLE, profileStatus }
  }

  return { deviceStatus: VerificationStatusTagType.AVAILABLE, profileStatus }
}
